import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SidebarLayout from '../../components/SidebarLayout';
import { adminService, sectorsService } from '../../services/api';
import './styles.css';
import { useToast } from '../../contexts/ToastContext';

export const AdminSectorsPanel = ({ embedded = false }) => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();

  const [sectors, setSectors] = useState([]);
  const [activeSectorId, setActiveSectorId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reports, setReports] = useState({ Beta: [], Alpha: [], 'Alpha+': [] });

  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ tier: 'Beta', report_type: '', report_uuid: '', apply_to_all: true });

  const activeSectorName = useMemo(() => {
    const list = Array.isArray(sectors) ? sectors : [];
    const s = list.find(x => String(x.name).toLowerCase() === 'education') || list.find(x => x.id === activeSectorId);
    return s ? s.name : '';
  }, [sectors, activeSectorId]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const data = await sectorsService.list();
        if (!mounted) return;
        const list = Array.isArray(data) ? data : (data.data?.sectors || data.sectors || []);
        setSectors(list);
        // Default to Education sector if present, else first
        const edu = list.find(s => (s.name || '').toLowerCase() === 'education');
        const defaultId = edu ? edu.id : (list[0] ? list[0].id : null);
        setActiveSectorId(defaultId);
      } catch (e) {
        if (mounted) setError(e.message || 'Failed to load sectors');
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Escape key handler for modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && showModal && !saving) {
        setShowModal(false);
      }
    };

    if (showModal) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
      return () => {
        document.removeEventListener('keydown', handleEscape);
        document.body.style.overflow = 'unset';
      };
    }
  }, [showModal, saving]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!activeSectorId) { setReports({ Beta: [], Alpha: [], 'Alpha+': [] }); setLoading(false); return; }
      setLoading(true);
      try {
        const data = await adminService.getSectorReports(activeSectorId);
        if (mounted) setReports(data || { Beta: [], Alpha: [], 'Alpha+': [] });
      } catch (e) {
        if (mounted) setError(e.message || 'Failed to load sector reports');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [activeSectorId]);

  return (
    <div
      className={`admin-sectors-container${
        embedded ? ' admin-sectors-container--embedded' : ''
      }`}
    >
      {!embedded && (
        <>
          <button onClick={() => navigate('/admin-dashboard')} className="admin-back-btn">Back</button>
          <h1 style={{ marginTop: 0 }}>All Sectors</h1>
        </>
      )}

      <div className="admin-actions-bar" style={{ justifyContent: 'space-between' }}>
        <select
          className="admin-select"
          style={{ maxWidth: 320 }}
          value={activeSectorId || ''}
          onChange={(e) => setActiveSectorId(Number(e.target.value))}
        >
          {sectors.map(s => (
            <option key={s.id} value={s.id}>{s.name}</option>
          ))}
        </select>

        <button className="admin-add-report-btn" onClick={() => setShowModal(true)}>Add Report</button>
      </div>

      {loading && (
        <div className="admin-user-details-card">
          <div className="admin-skeleton-grid">
            <div className="skeleton-block" />
            <div className="skeleton-block" />
            <div className="skeleton-block" />
            <div className="skeleton-block" />
          </div>
        </div>
      )}
      {error && <div style={{ color: 'crimson', marginBottom: 12 }}>{error}</div>}

      <div className="admin-reports-grid">
        <div className="admin-reports-section">
          <div className="admin-section-header tier-beta">
            <div className="admin-section-title">
              <span>Beta</span>
              <span className="admin-section-count">{reports.Beta.length}</span>
            </div>
          </div>
          {reports.Beta.map((r, idx) => (
            <div key={`b-${idx}`} className="admin-report-item" style={{ justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span className="admin-report-dot"></span>
                <span>{r.report_type}</span>
              </div>
              <button
                className="admin-btn admin-btn-secondary"
                onClick={async () => {
                  const proceed = window.confirm('Delete this report from sector defaults?');
                  if (!proceed) return;
                  const alsoRemoveUsers = window.confirm('Also remove this report from all users in this sector?');
                  try {
                    await adminService.deleteSectorReport(activeSectorId, r.report_uuid, alsoRemoveUsers);
                    setReports(prev => ({
                      ...prev,
                      Beta: prev.Beta.filter(x => x.report_uuid !== r.report_uuid)
                    }));
                  } catch (e) {
                    showError(e.message || 'Failed to delete');
                  }
                }}
              >
                Delete
              </button>
            </div>
          ))}
          {reports.Beta.length === 0 && <div style={{ color: '#666' }}>No reports</div>}
        </div>

        <div className="admin-reports-section">
          <div className="admin-section-header tier-alpha">
            <div className="admin-section-title">
              <span>Alpha</span>
              <span className="admin-section-count">{reports.Alpha.length}</span>
            </div>
          </div>
          {reports.Alpha.map((r, idx) => (
            <div key={`a-${idx}`} className="admin-report-item" style={{ justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span className="admin-report-dot"></span>
                <span>{r.report_type}</span>
              </div>
              <button
                className="admin-btn admin-btn-secondary"
                onClick={async () => {
                  const proceed = window.confirm('Delete this report from sector defaults?');
                  if (!proceed) return;
                  const alsoRemoveUsers = window.confirm('Also remove this report from all users in this sector?');
                  try {
                    await adminService.deleteSectorReport(activeSectorId, r.report_uuid, alsoRemoveUsers);
                    setReports(prev => ({
                      ...prev,
                      Alpha: prev.Alpha.filter(x => x.report_uuid !== r.report_uuid)
                    }));
                  } catch (e) {
                    showError(e.message || 'Failed to delete');
                  }
                }}
              >
                Delete
              </button>
            </div>
          ))}
          {reports.Alpha.length === 0 && <div style={{ color: '#666' }}>No reports</div>}
        </div>

        <div className="admin-reports-section">
          <div className="admin-section-header tier-alphap">
            <div className="admin-section-title">
              <span>Alpha+</span>
              <span className="admin-section-count">{reports['Alpha+'].length}</span>
            </div>
          </div>
          {reports['Alpha+'].map((r, idx) => (
            <div key={`ap-${idx}`} className="admin-report-item" style={{ justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span className="admin-report-dot"></span>
                <span>{r.report_type}</span>
              </div>
              <button
                className="admin-btn admin-btn-secondary"
                onClick={async () => {
                  const proceed = window.confirm('Delete this report from sector defaults?');
                  if (!proceed) return;
                  const alsoRemoveUsers = window.confirm('Also remove this report from all users in this sector?');
                  try {
                    await adminService.deleteSectorReport(activeSectorId, r.report_uuid, alsoRemoveUsers);
                    setReports(prev => ({
                      ...prev,
                      ['Alpha+']: prev['Alpha+'].filter(x => x.report_uuid !== r.report_uuid)
                    }));
                  } catch (e) {
                    showError(e.message || 'Failed to delete');
                  }
                }}
              >
                Delete
              </button>
            </div>
          ))}
          {reports['Alpha+'].length === 0 && <div style={{ color: '#666' }}>No reports</div>}
        </div>
      </div>

      {showModal && (
        <div className="admin-modal-overlay" onClick={() => !saving && setShowModal(false)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <div>Add Sector Report {activeSectorName ? `- ${activeSectorName}` : ''}</div>
              <button className="admin-btn admin-btn-secondary" onClick={() => !saving && setShowModal(false)}>✕</button>
            </div>
            <div className="admin-modal-body">
              <div className="admin-modal-row">
                <label>Tier *</label>
                <select
                  className="admin-select"
                  value={form.tier}
                  onChange={(e) => setForm((f) => ({ ...f, tier: e.target.value }))}
                >
                  <option value="Beta">Beta</option>
                  <option value="Alpha">Alpha</option>
                  <option value="Alpha+">Alpha+</option>
                </select>
              </div>
              <div className="admin-modal-row">
                <label>Report Type *</label>
                <input
                  className="admin-input"
                  type="text"
                  value={form.report_type}
                  onChange={(e) => setForm((f) => ({ ...f, report_type: e.target.value }))}
                  placeholder="e.g. Sales"
                />
              </div>
              <div className="admin-modal-row">
                <label>Report UUID *</label>
                <input
                  className="admin-input"
                  type="text"
                  value={form.report_uuid}
                  onChange={(e) => setForm((f) => ({ ...f, report_uuid: e.target.value }))}
                  placeholder="e.g. 123e4567-e89b-12d3-a456-426614174000"
                />
              </div>
              <div className="admin-modal-row" style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                <input
                  id="apply_all"
                  type="checkbox"
                  checked={!!form.apply_to_all}
                  onChange={(e) => setForm((f) => ({ ...f, apply_to_all: e.target.checked }))}
                />
                <label htmlFor="apply_all">Select for all users in this sector</label>
              </div>
            </div>
            <div className="admin-modal-actions">
              <button
                className="admin-btn admin-btn-secondary"
                disabled={saving}
                onClick={() => !saving && setShowModal(false)}
              >
                Cancel
              </button>
              <button
                className="admin-btn admin-btn-primary"
                disabled={saving || !form.tier || !form.report_type || !form.report_uuid || !activeSectorId}
                onClick={async () => {
                  try {
                    setSaving(true);
                    const created = await adminService.addSectorReport(activeSectorId, form);
                    const tierKey = String(created.tier || '');
                    setReports((prev) => {
                      const copy = { Beta: [...prev.Beta], Alpha: [...prev.Alpha], 'Alpha+': [...prev['Alpha+']] };
                      if (tierKey === 'Beta') copy.Beta = [...copy.Beta, created];
                      else if (tierKey === 'Alpha') copy.Alpha = [...copy.Alpha, created];
                      else if (tierKey === 'Alpha+') copy['Alpha+'] = [...copy['Alpha+'], created];
                      return copy;
                    });
                    showSuccess('Sector report added successfully');
                    setShowModal(false);
                    setForm({ tier: 'Beta', report_type: '', report_uuid: '', apply_to_all: true });
                  } catch (e) {
                    showError(e.message || 'Failed to add sector report');
                  } finally {
                    setSaving(false);
                  }
                }}
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const AdminSectors = () => {
  const navigate = useNavigate();
  return (
    <SidebarLayout>
      <AdminSectorsPanel />
    </SidebarLayout>
  );
};

export default AdminSectors;


